/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { defaultCurrency, useCurrencies } from '@zapier/currency/src/hooks';
import { Currency, UseQuery } from '@zapier/currency/src/types';
import { sortCurrenciesByArray } from '@zapier/currency/src/utils';

import { Dropdown } from './Dropdown';
import { FlagIcon } from './FlagIcon';

import { currencyOrder, PLAN_CHANGE_EVENT } from './constants';

const rootStyles = css`
  width: 143px;
`;

type Props = {
  onSelectCurrency: (currency: Currency) => void;
  selectedCurrency: Currency;
  useQuery: UseQuery;
};

/**
 * A dropdown that allows the user to select a currency to pay in.
 */
export const CurrencyDropdown = ({
  onSelectCurrency,
  selectedCurrency = defaultCurrency,
  useQuery,
}: Props) => {
  const { trackEvent } = useTrackingContext();
  const { isLoading, currencies: options, error } = useCurrencies({ useQuery });

  const onChangeCurrency = (currency: Currency) => {
    onSelectCurrency(currency);
    // Send event with currency
    trackEvent(PLAN_CHANGE_EVENT, {
      absolute_url: window.location.href,
      event_action: 'select_currency',
      interaction_goal: 'select currency',
      interaction_name: 'currency dropdown',
      option_selected: currency.name,
    });
  };

  // Don't show if loading or error
  if (isLoading || error) {
    return null;
  }

  return (
    <div css={rootStyles}>
      <Dropdown
        ariaLabel="Currency"
        getLabelForSelectedItem={item => `${item.name} ${item.symbol}`}
        items={[...options].sort((a: Currency, b: Currency) =>
          sortCurrenciesByArray(a, b, currencyOrder)
        )}
        menuAriaLabel="List of currencies"
        onChange={(currency: Currency) => onChangeCurrency(currency)}
        placeholder="Choose a currency..."
        renderIcon={item => {
          return !!item && <FlagIcon name={item.icon} width={24} />;
        }}
        selectedItem={selectedCurrency}
      />
    </div>
  );
};
